/**
 * Entity data fetching hook for EntityForm edit mode.
 *
 * Uses TanStack React Query to fetch entity data from an ERP endpoint
 * when an `entityId` is provided. The raw API response is transformed
 * into form-compatible values via `mapResponseToValues()`.
 *
 * Consumers can supply a custom `fetcher` function to control how the
 * HTTP request is made. When no fetcher is provided, the hook uses a
 * default that throws an informative error, ensuring the ERP integration
 * layer is wired up before any fetch is attempted.
 *
 * @see Spec section 4.2 for hook return type definition.
 * @see Spec sections 2.2 and 3.4 for endpoint and fetch configuration.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { EndpointConfig } from '../types';
import { mapResponseToValues } from '../utils/responseMapper';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A function that fetches a single entity from the ERP backend.
 *
 * @param endpoint - The ERP endpoint identifier (e.g. "admin.users.read").
 * @param entityId - The unique identifier of the entity to fetch.
 * @returns A promise that resolves with the raw API response payload.
 */
export type EntityFetcher = (
  endpoint: string,
  entityId: string | number,
) => Promise<unknown>;

/**
 * Return value of the `useFetchEntity` hook.
 */
export interface UseFetchEntityReturn {
  /** Mapped form values derived from the API response, or `undefined` while loading / when disabled. */
  data: Record<string, unknown> | undefined;
  /** Whether the query is currently in-flight. */
  isLoading: boolean;
  /** A human-readable error string, or `null` when there is no error. */
  error: string | null;
}

// ---------------------------------------------------------------------------
// Default Fetcher
// ---------------------------------------------------------------------------

/**
 * Placeholder fetcher that always rejects with an informative error.
 *
 * This ensures consumers receive a clear message when they attempt to use
 * edit mode without providing a real `fetcher` implementation.
 */
const defaultFetcher: EntityFetcher = (_endpoint, _entityId) => {
  return Promise.reject(
    new Error(
      'useFetchEntity: no fetcher provided. ' +
        'Supply a `fetcher` function that calls your ERP client.',
    ),
  );
};

// ---------------------------------------------------------------------------
// Error Extraction
// ---------------------------------------------------------------------------

/**
 * Extracts a human-readable message from an unknown error value.
 *
 * @param err - The thrown/rejected value.
 * @returns A string describing the error.
 */
function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'string') {
    return err;
  }
  return 'An unknown error occurred while fetching entity data.';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches entity data for EntityForm edit mode.
 *
 * The query is **only enabled** when both `entityId` and `fetchEndpoint`
 * are defined. When disabled the hook returns a stable idle state
 * (`data: undefined`, `isLoading: false`, `error: null`).
 *
 * @param fetchEndpoint    - The fetch endpoint configuration from `EndpointConfig.fetchEndpoint`.
 * @param entityId         - The entity identifier. `undefined` disables the query.
 * @param responseMapping  - Optional mapping from form field names to API response paths.
 * @param fetcher          - Optional custom fetcher. Defaults to a placeholder that throws.
 * @returns An object containing `data`, `isLoading`, and `error`.
 *
 * @example
 * ```ts
 * const { data, isLoading, error } = useFetchEntity(
 *   config.endpoint.fetchEndpoint,
 *   entityId,
 *   config.endpoint.responseMapping,
 *   myErpFetcher,
 * );
 * ```
 */
export function useFetchEntity(
  fetchEndpoint: EndpointConfig['fetchEndpoint'],
  entityId: string | number | undefined,
  responseMapping?: Record<string, string>,
  fetcher: EntityFetcher = defaultFetcher,
): UseFetchEntityReturn {
  // ---- Query Configuration ------------------------------------------------

  const isEnabled = entityId !== undefined && fetchEndpoint !== undefined;

  const queryResult = useQuery<unknown, unknown>({
    queryKey: ['entity', fetchEndpoint?.erpEndpoint, entityId],
    queryFn: () => {
      // Both values are guaranteed to be defined because `enabled` gates execution.
      if (!fetchEndpoint || entityId === undefined) {
        return Promise.reject(new Error('useFetchEntity: query ran without required params'));
      }
      return fetcher(fetchEndpoint.erpEndpoint, entityId);
    },
    enabled: isEnabled,
  });

  // ---- Mapped Data --------------------------------------------------------

  const data = useMemo<Record<string, unknown> | undefined>(() => {
    if (!queryResult.data) {
      return undefined;
    }
    return mapResponseToValues(queryResult.data, responseMapping);
  }, [queryResult.data, responseMapping]);

  // ---- Error Extraction ---------------------------------------------------

  const error = useMemo<string | null>(() => {
    if (!queryResult.error) {
      return null;
    }
    return extractErrorMessage(queryResult.error);
  }, [queryResult.error]);

  // ---- Return -------------------------------------------------------------
  // Memoised so the whole return object keeps a stable identity and only
  // changes when `data`, `isLoading`, or `error` changes. Keeps the object
  // stable for the `useEffect` in `useEntityForm` that lists `formValues`/
  // fetch-derived data, and for the composed public API memo.

  const isLoading = queryResult.isLoading;

  return useMemo<UseFetchEntityReturn>(
    () => ({
      data,
      isLoading,
      error,
    }),
    [data, isLoading, error],
  );
}
